import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";

import { easing } from "../theme";
import { useWebSocket } from "../useWebSocket";
import * as haptics from "../haptics";
import type { PlayerInfo, DigitalPromptEntryView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsWaitingFor,
    MaskHeadline,
} from "../ms";
import { PromptCardBack } from "./PromptCardBack";
import { PromptReveal } from "./PromptReveal";
import { CARD_RATIO, cardTransition } from "./promptCard";

/* ──────────────────────────────────────────────────────────────────────
 * The deckless draw.
 *
 * Same beat as `PromptEntry` — a card is drawn, then it turns over and the
 * prompt is the headline — but the picker chooses which card instead of reading
 * a code off one in their hand. The reveal is deliberately the *same* animation:
 * the turned card grows into the headline via `PromptReveal`, so a table that
 * switched modes because nobody brought the deck still sees the game they know.
 *
 * The screen knows nothing about "digital mode". It renders a phase the server
 * put it in, off a view that tells it how many cards there are and which one was
 * turned. The card texts aren't here to leak: the server holds them until one is
 * picked.
 * ────────────────────────────────────────────────────────────────────── */

const Column = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    width: 100%;
`;

/*
 * The hand runs down the screen, not across it.
 *
 * Three cards side by side on a phone are ~107px wide each, and at that size a
 * playing card stops reading as one — the print is tiny and the proportions
 * don't register even though they're exactly right. Stacked, each card gets
 * most of the width and lands around 270px, which is nearly three times the
 * area and unmistakably a card.
 *
 * The width is capped by height as well as by width (`32vh`), because three
 * cards have to fit down a screen whose height varies far more than its width.
 * At 844px tall that's a 270px card; at 667px it's 213px; both fit without
 * scrolling, which matters when one of them is the thing you have to tap.
 */
const Hand = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

/*
 * Slot → CardShell → Tilt → card, and the order matters.
 *
 * `CardShell` is the element that carries the `layoutId`, and it stays clean:
 * framer morphs it by writing its own transform, and anything else declared
 * there fights that. The fan angle, the hover lift and the deal all live on
 * `Tilt`, *inside* it, where they're just decoration on a box framer is free to
 * move. The same split ArticleSelection arrived at, and for the same reason —
 * a tilt on the layoutId element is what made that morph snap instead of glide.
 *
 * The slot itself is layout only. A CSS animation there left a residual
 * transform on an ancestor, which framer can't project through either.
 */
const Slot = styled.button`
    position: relative;
    width: min(300px, 86%, 32vh);
    aspect-ratio: ${CARD_RATIO};
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;

    &:disabled { cursor: default; }

    /* A plain class, not an emotion component selector — those need a babel
       plugin this project doesn't run and throw at runtime. */
    &:hover:not(:disabled) .card-fan, &:focus-visible .card-fan {
        transform: rotate(0deg) scale(1.03);
    }
`;

const CardShell = styled(motion.div)`
    position: absolute;
    inset: 0;
`;

const Tilt = styled.div<{ tilt: number; delay: number }>`
    position: absolute;
    inset: 0;
    transform: rotate(${p => p.tilt}deg);
    transition: transform 0.2s ${easing.pop};
    animation: ms-paper-drop 0.65s ${easing.pop} both;
    animation-delay: ${p => p.delay}s;
`;

const Instruction = styled(MsDisplay)`
    font-size: 26px;
    text-align: center;
`;


/* Nothing in this app sits perfectly straight — the paper drops in at an angle
 * and stays there. A degree or two each way, alternating, so the stack reads as
 * three cards put down by hand rather than a list of three buttons. */
const TILTS = [-1.8, 1.2, -1.1];

interface Props {
    view: DigitalPromptEntryView;
    self: PlayerInfo;
}

export default function DigitalPromptEntry({ view, self }: Props) {
    const { sendMessage } = useWebSocket();
    const { picker, cardCount, pickedIndex, promptText } = view;

    const isPicker = picker?.id === self.id;
    const dealerName = picker?.name ?? "Someone";

    /**
     * Every card gets its own layout id, from the moment the hand mounts.
     *
     * The obvious thing — mark the chosen card when it's chosen — doesn't work:
     * framer morphs by matching a removed projection node to an added one, and a
     * node that appears in the same commit that removes it was never measured, so
     * the card just cuts to the prompt. Optimistically marking on tap doesn't help
     * either; the id still arrives one render before the element goes.
     *
     * So all three are tracked the whole time and the reveal names the one the
     * server says was turned. That's what `pickedIndex` is for, and it means the
     * card that grows is the card the picker actually touched.
     */
    const cardLayoutId = (index: number) => `digital-prompt-card-${index}`;

    const pick = (index: number) => {
        if (!isPicker || pickedIndex != null) return;
        sendMessage({ type: "pick_prompt_card", index });
    };

    const fadeTransition = { duration: 0.32, ease: [0.45, 0, 0.2, 1] as const };

    /* The prompt has landed. Rendered by the shared `PromptReveal`, so this is
     * the same picture the physical phase gives — the card is the whole moment
     * and nothing is added around it. */

    /* ── The hand, face down ─────────────────────────────────────────── */
    const renderHand = () => (
        <Column
            key="hand"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: fadeTransition }}
            exit={{ opacity: 0, transition: { duration: 0.18 } }}
        >
            <Instruction>
                <MaskHeadline>
                    {isPicker ? "Take a card." : `${dealerName} is drawing.`}
                </MaskHeadline>
            </Instruction>

            <Hand role="group" aria-label="Face-down prompt cards">
                {Array.from({ length: cardCount }, (_, i) => (
                    <Slot
                        key={i}
                        type="button"
                        disabled={!isPicker}
                        onClick={() => { haptics.select(); pick(i); }}
                        aria-label={`Card ${i + 1} of ${cardCount}`}
                    >
                        <CardShell layoutId={cardLayoutId(i)} transition={cardTransition}>
                            <Tilt
                                className="card-fan"
                                tilt={TILTS[i % TILTS.length]}
                                delay={0.12 + i * 0.09}
                            >
                                <PromptCardBack />
                            </Tilt>
                        </CardShell>
                    </Slot>
                ))}
            </Hand>
        </Column>
    );

    const revealed = !!promptText;

    return (
        <MsScreen>
            {/* Same scaffolding the physical phase uses, so the revealed card sits
                in exactly the same place — the reveal is one moment in the round and
                shouldn't shift depending on how the prompt was drawn. */}
            <MsBody style={{
                justifyContent: "center",
                gap: 18,
                paddingTop: revealed ? 56 : 20,
            }}>
                <AnimatePresence mode="popLayout" initial={false}>
                    {revealed
                        ? <PromptReveal
                            key="headline"
                            promptText={promptText!}
                            layoutId={pickedIndex != null ? cardLayoutId(pickedIndex) : undefined}
                            flip
                        />
                        : renderHand()}
                </AnimatePresence>
            </MsBody>
            {/* Empty on reveal for the same reason as the physical phase — the card
                is the whole moment — and the waiting chip sits here rather than under
                the cards, which is also where the physical phase puts it. */}
            <MsFooter>
                {!revealed && !isPicker && <MsWaitingFor name={dealerName} />}
            </MsFooter>
        </MsScreen>
    );
}
